import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Checkbox } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import BillingContext from '@totem/components/ticketing/billing/billingContext';
import { UnbilledTimeEntry } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { formatDateOnly, isNotNull, isNull } from '@totem/utilities/common';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

const UnbilledSelectedTable = () => {
  const {
    loading,
    selectedUnbilled,
    selectUnbilled,
    deselectUnbilled,
    clearSelectedUnbilled,
  } = useContext(BillingContext);

  const columns: ColumnProps<UnbilledTimeEntry>[] = [
    {
      title: (
        <CloseCircleOutlined
          style={{ marginTop: 8, marginRight: 8 }}
          onClick={() => clearSelectedUnbilled()}
        />
      ),
      dataIndex: 'id',
      key: 'id',
      render: (_, record: UnbilledTimeEntry) => (
        <Checkbox
          checked={
            isNull(selectedUnbilled)
              ? false
              : selectedUnbilled.findIndex((chk) => chk.id === record.id) >= 0
          }
          onChange={(evt) => {
            if (evt.target.checked) {
              selectUnbilled(record);
            } else {
              deselectUnbilled(record);
            }
          }}
        />
      ),
    },
    {
      title: 'Date',
      dataIndex: 'timeEntry.date',
      key: 'timeEntry.date',
      render: (_, record: UnbilledTimeEntry) =>
        `${formatDateOnly(record.timeEntry.date)}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.timeEntry.date, compB.timeEntry.date),
    },

    {
      title: 'Hours',
      dataIndex: 'timeEntry.hoursLogged',
      key: 'timeEntry.hoursLogged',
      render: (_, record: UnbilledTimeEntry) =>
        `${record.timeEntry.hoursLogged}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(
          compA.timeEntry.hoursLogged,
          compB.timeEntry.hoursLogged,
        ),
    },
    {
      title: 'Resource',
      dataIndex: 'resource.name',
      key: 'resource.name',
      render: (_, record: UnbilledTimeEntry) => `${record.resource.name}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.resource.name, compB.resource.name),
    },
    {
      title: 'Description',
      dataIndex: 'timeEntry.description',
      key: 'timeEntry.description',
      render: (_, record: UnbilledTimeEntry) => record.timeEntry.description,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.timeEntry.description,
          compB.timeEntry.description,
        ),
    },
    {
      title: 'Task',
      dataIndex: 'task.number',
      key: 'task.number',
      render: (_, record: UnbilledTimeEntry) => {
        if (record.task !== null && record.task.name !== '') {
          return `${record.task.number}: ${record.task.name}`;
        }
        return '';
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.task.number, compB.task.number),
    },
    {
      title: 'Ticket Number',
      dataIndex: 'ticket.number',
      key: 'ticket.number',
      render: (_, record: UnbilledTimeEntry) => (
        <Link
          to={`/dashboard/ticket/${record.ticket.id}`}
          target={record.ticket.id}
        >
          {record.ticket.number}
        </Link>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.ticket.number, compB.ticket.number),
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(selectedUnbilled) ? selectedUnbilled : []}
      loading={loading}
      rowKey={(record) => record.id}
    />
  );
};

export default UnbilledSelectedTable;
